// ============================================================================
// Auth Context - User Authentication State Management
// ============================================================================

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { analytics } from '../lib/analytics';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

export interface AuthUser {
  user_id: string;
  email: string;
  name: string;
  picture?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Wrapper to track user identification
  const setUser = (newUser: AuthUser | null) => {
    if (newUser) {
      // Identify user in PostHog when they log in
      analytics.trackLogin(newUser.user_id, newUser.email, newUser.name);
    }
    setUserState(newUser);
  };

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Identify returning user in PostHog
          analytics.identify(data.data.user_id, {
            email: data.data.email,
            name: data.data.name,
          });
          setUserState(data.data);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = () => {
    // Redirect to Emergent Auth with current origin as redirect URL
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      // Track logout before clearing user
      analytics.trackLogout();
      
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUserState(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        setUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
